/*
 * Copyright (c) CIRROM 2023.
 */

import classes from "./CRMPanel.module.css";
import Typography from "@mui/material/Typography";

import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";

import { line } from "d3-shape";
import { NUMBER_FORMATTER } from "../utils/utils";

const CustomBarComponent = ({ bar: { x, y, width, height, color, data } }) => {
  const isTwoBarChart =
    data.id === "total_contacts" || data.id === "contacts_with_issues";
  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect
        width={width}
        height={height}
        fill={color}
        strokeWidth="0"
        stroke="white"
        rx={10}
      />

      <text
        x={width / 2}
        y={-10} // the important bit!!
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: isTwoBarChart ? 10 : 15,
          fontWeight: "bold",
          pointerEvents: "none",
          fill: color,
          fontFamily: "space-grotesk, sans-serif",
          transform: isTwoBarChart ? "rotate(341deg)" : "",
        }}
      >
        {NUMBER_FORMATTER.format(data.value)}
      </text>
    </g>
  );
};

const CustomTick = (tick) => {
  return (
    <g transform={`translate(${tick.x},${tick.y + 22})`}>
      <line stroke="#b3b3b3" strokeWidth={1.5} y1={-22} y2={-12} />
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fill: "#333",
          fontSize: 10,
          fontFamily: "proxima-nova",
        }}
      >
        {tick.value}
      </text>
    </g>
  );
};

export const NewCRMPanelBarSection = (props: {
  changeMetrics: any;
  series?: string[];
  alternativeChartVariant?: boolean;
  maxHeight?: number;
  legendData?: any;
  selectionMode?: "sample" | "trailing";
  maxVisibleItems?: number;
}) => {
  /**
   * Build a bar chart of data
   * changeMetrics - an array of shape [ { readableMonth:'01-Mar-2020', ..series } ]
   * series - a list of any data points in changeMetrics, if not present defaults to ['total']
   *
   * */
  const [visibleChangeMetrics, setVisibleChangeMetrics] = useState([]);

  useEffect(() => {
    if (props.changeMetrics) {
      if (props.selectionMode === "trailing") {
        const maxVisibleItems =
          props.maxVisibleItems || props.changeMetrics.length;
        setVisibleChangeMetrics(
          props.changeMetrics.slice(
            Math.max(props.changeMetrics.length - maxVisibleItems, 0)
          )
        );
      } else if (props.changeMetrics.length > 8) {
        const startThreeItems = props.changeMetrics.slice(0, 1);
        const lastThreeItems = props.changeMetrics.slice(
          props.changeMetrics.length - 2,
          props.changeMetrics.length
        );
        const middleItems = [];
        const avgItemsNeeded = (props.changeMetrics.length - 4) / 4;

        for (
          let index = avgItemsNeeded;
          index < props.changeMetrics.length - 2;
          index += avgItemsNeeded
        ) {
          const metric = props.changeMetrics[Math.floor(index)];
          if (middleItems.indexOf(metric) === -1) {
            middleItems.push(props.changeMetrics[Math.floor(index)]);
          }
        }
        setVisibleChangeMetrics([
          ...startThreeItems,
          ...middleItems,
          ...lastThreeItems,
        ]);
      } else {
        setVisibleChangeMetrics(props.changeMetrics);
      }
    }
  }, [props.changeMetrics, props.maxVisibleItems, props.selectionMode]);

  return (
    <div
      className={classes.crmInfoSectionBar}
      style={{
        height: `min(calc(100% - 12px),${props.maxHeight || 200}px)`,
        minWidth: "150px",
      }}
    >
      <div style={{ height: "100%", width: "100%" }}>
        <div style={{ width: "100%", height: "90%" }}>
          {visibleChangeMetrics && visibleChangeMetrics.length > 0 ? (
            <ResponsiveBar
              groupMode={"grouped"}
              data={visibleChangeMetrics}
              borderRadius={20}
              valueFormat={" >-#,.2d"}
              keys={props.series ? props.series : ["total"]}
              indexBy="readableMonth"
              margin={{ top: 30, right: 10, bottom: 40, left: 10 }}
              padding={0.3}
              innerPadding={12}
              colors={["#0090c3", "#e50144"]}
              enableLabel={true}
              barComponent={CustomBarComponent}
              valueScale={{ type: "linear" }}
              gridYValues={[0]}
              indexScale={{ type: "band", round: true }}
              axisLeft={{ tickValues: [] }}
              animate={true}
              motionConfig="slow"
              borderColor={{
                from: "color",
                modifiers: [["darker", 1.6]],
              }}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendPosition: "middle",
                renderTick: CustomTick,
              }}
              legends={
                props.series && props.series.length > 1
                  ? [
                      {
                        dataFrom: "keys",
                        data: props.legendData,
                        anchor: "bottom-left",
                        direction: "row",
                        justify: false,
                        translateX: 0,
                        translateY: 40,
                        itemsSpacing: 1,
                        itemWidth: 70,
                        itemHeight: 20,
                        itemDirection: "left-to-right",
                        itemOpacity: 0.85,
                        symbolSize: 5,
                      },
                    ]
                  : []
              }
            />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              <img
                src={
                  props.alternativeChartVariant
                    ? "/undraw_pie_chart.svg"
                    : "/undraw_statistic_chart.svg"
                }
                style={{
                  maxWidth: `${
                    props.alternativeChartVariant ? "240px" : "128px"
                  }`,
                  maxHeight: props.maxHeight,
                }}
                alt={"undraw_chart"}
              />
              {!props.maxHeight && (
                <Typography variant={"caption"} textAlign={"center"}>
                  Cirrom has not changed anything yet. Please enable automatic
                  rules or approve tickets manually.
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
